import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format as formatDate } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { API_TAGS } from '@/shared/api';
import { delay } from '@/shared/lib/utils';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import { searchKeys } from '../keys';
import {
  deleteConversations,
  downloadConversationsSearch,
  downloadTranscriptArchive,
  fetchTranscriptArchiveStatus,
  runConversationsPipeline,
  searchConversations,
  startTranscriptArchive,
} from './api';
import type { SearchConversationRequest, TranscriptArchiveRequest, TranscriptArchiveStatusResponse } from './types';

export const useSearchConversationsQuery = (request: SearchConversationRequest) => {
  return useQuery({
    queryKey: searchKeys.query(request),
    queryFn: () => searchConversations(request),
    retry: false,
    placeholderData: keepPreviousData,
  });
};

type InfiniteParams = Omit<SearchConversationRequest, 'limit' | 'offset'> & { pageSize?: number };

export const useSearchConversationsInfiniteQuery = (params: InfiniteParams) => {
  const pageSize = params.pageSize ?? 50;

  return useInfiniteQuery({
    queryKey: searchKeys.infiniteQuery({ ...params, pageSize }),
    queryFn: ({ pageParam }) =>
      searchConversations({
        ...params,
        limit: pageSize,
        offset: (pageParam as number) * pageSize,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = ((lastPageParam as number) + 1) * pageSize;
      return lastPage.total > nextOffset ? lastPageParam + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
};

/**
 * Выгрузка найденных диалогов файлом: сохраняет пришедший файл на диск.
 * `isPending` держится всё время формирования файла — им блокируется повторный запуск.
 */
export const useDownloadConversationsSearch = () => {
  return useMutation({
    mutationFn: downloadConversationsSearch,
    onSuccess: (blob) => {
      // Имя повторяет то, которое бэкенд ставит в Content-Disposition: кросс-доменный ответ
      // этот заголовок браузеру не открывает, а из самого файла имя не восстановить.
      downloadFileFromBlob(blob, `dialogs_${formatDate(new Date(), 'yyyy-MM-dd')}.xlsx`);
    },
  });
};

/** Пауза между запросами состояния формирующегося архива транскрипций */
const TRANSCRIPT_ARCHIVE_POLL_INTERVAL_MS = 2000;

/** Ход формирования архива транскрипций: сколько диалогов из взятых в архив уже обработано */
export type TranscriptArchiveProgress = Pick<TranscriptArchiveStatusResponse, 'processed' | 'accepted'>;

/**
 * Чем закончилось ожидание архива транскрипций:
 * - downloaded — архив готов и сохранён на диск; `failed` — число диалогов, транскрипции которых
 *   не сформированы и в архив не попали;
 * - failed — сформировать архив не удалось; `error` — причина, если бэкенд её назвал;
 * - cancelled — ожидание прекращено размонтированием потребителя, архив не скачан.
 */
export type TranscriptArchiveResult =
  | { status: 'downloaded'; failed: number }
  | { status: 'failed'; error: string | null }
  | { status: 'cancelled' };

/**
 * Архив транскрипций по выборке: запуск фонового формирования, опрос его состояния и сохранение
 * готового архива на диск.
 *
 * `isPending` держится от запуска до скачивания или отказа — им блокируется повторный запуск.
 * Ожидание живёт, пока смонтирован потребитель: формируется архив в фоне на бэкенде, а скачивать
 * его после ухода со списка некому.
 */
export const useTranscriptArchive = () => {
  const [progress, setProgress] = useState<TranscriptArchiveProgress | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const { mutate: start, isPending } = useMutation({
    mutationFn: async (request: Omit<TranscriptArchiveRequest, 'entityType'>): Promise<TranscriptArchiveResult> => {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const { signal } = abortController;

      const { archiveId, accepted } = await startTranscriptArchive(request);
      setProgress({ processed: 0, accepted });

      let archive: TranscriptArchiveStatusResponse;
      do {
        await delay(TRANSCRIPT_ARCHIVE_POLL_INTERVAL_MS, signal);
        if (signal.aborted) return { status: 'cancelled' };
        archive = await fetchTranscriptArchiveStatus(archiveId);
        setProgress({ processed: archive.processed, accepted: archive.accepted });
      } while (archive.status === 'IN_PROGRESS');

      if (archive.status === 'FAILED') return { status: 'failed', error: archive.error };

      const blob = await downloadTranscriptArchive(archiveId);
      if (signal.aborted) return { status: 'cancelled' };
      // Имя по дате, а не из Content-Disposition: кросс-доменный ответ этот заголовок браузеру не открывает
      downloadFileFromBlob(blob, `transcripts_${formatDate(new Date(), 'yyyy-MM-dd')}.zip`);
      return { status: 'downloaded', failed: archive.failed };
    },
    onSettled: () => setProgress(null),
  });

  return { start, isPending, progress };
};

/**
 * Массовый запуск обработки диалогов текущей выборки.
 * `isPending` держится всё время запроса — им блокируется повторный запуск.
 */
export const useRunConversationsPipeline = () => {
  return useMutation({
    mutationFn: runConversationsPipeline,
  });
};

/**
 * Удаление выбранных диалогов. `isPending` держится всё время запроса — им блокируется повторное
 * удаление; об успехе сообщает вызывающий.
 *
 * Удалённый диалог уходит отовсюду, где был виден: из выдачи поиска, списков и карточек диалогов
 * (всё это — под тегом диалогов) и из состава выборок. Кеши диалогов только обесцениваются, без
 * перезапроса открытых: карточка удалённого диалога в этот момент ещё на экране, и её перезапрос
 * ответил бы ошибкой. Показанный список перезапрашивает вызывающий.
 */
export const useDeleteConversations = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteConversations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_TAGS.DIALOGS], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: [API_TAGS.SAMPLES] });
    },
  });
};
